// The auth screens that need a session, a token or an IdP, ported from their templates:
//   status/page/status.page.tpl.html
//   user/page/user.page.tpl.html
//   sign-up/page/sign-up.page.tpl.html
//   reset-password/page/reset-password.page.tpl.html
//   multi-factor-auth/validate/page/validate.page.tpl.html
//   multi-factor-auth/reset-request/page/reset-request.page.tpl.html
//   sso/callback/page/callback.page.tpl.html
//
// Class names and DOM structure are verbatim (CLAUDE.md 4). What is NOT ported, and why:
//   - the admin whitelabel `<select>` on sign-up — admin build only, reads an admin-only
//     endpoint, and belongs with admin-login-helper (same call as LoginPage's picker);
//   - `<div id="g-recaptcha-container">` is rendered but no widget is mounted into it. The
//     grecaptcha script is loaded by the JSP upstream, and the dev tenant answers
//     `captchaValidationDisabled: true`; the container stays so the layout matches and so
//     Phase 5 has somewhere to mount it.
import { useState, type ReactElement, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AuthNav } from './AuthNav.tsx'
import { LoadingButton } from './LoadingButton.tsx'
import { useEmailValidProbe, useSsoRequiredProbe } from './ssoRequiredProbe.ts'
import { StatusIndicationView, type StatusIndication } from './StatusIndication.tsx'
import { PageHost } from './PageHost.tsx'

// ─── status ──────────────────────────────────────────────────────────────────

export interface StatusPageProps {
  status: StatusIndication
  loading?: boolean
  onSignOut: () => void | Promise<unknown>
  /** Upstream swaps the whole body for an update prompt when the host permission is missing. */
  permissionsGranted?: boolean
  onRequestPermissions?: () => void | Promise<unknown>
  /** `status.html:18`'s `<origin-request>` — see the same prop on `LoginPage`. */
  originRequest?: ReactNode
  admin?: boolean
}

export function StatusPage({
  status,
  loading = false,
  onSignOut,
  permissionsGranted = true,
  onRequestPermissions,
  originRequest,
  admin = false,
}: StatusPageProps): ReactElement {
  return (
    <PageHost name="chrome-extension-status-page" className="popup-styles" hostClassName="non-extended-ui">
      <AuthNav admin={admin} />
      {permissionsGranted ? (
        <div className="page-content">
          <StatusIndicationView status={status} loading={loading} />
          {/* `<a href ng-click>` upstream: the bare `href` is what makes it focusable and
              Enter-operable, and AngularJS's own `a[href=""]` directive suppresses the
              navigation. Dropping it, as the first port did, left a div-like anchor no
              keyboard user could reach. */}
          <a
            className="sign-out-btn"
            href=""
            onClick={(event) => {
              event.preventDefault()
              void onSignOut()
            }}
          >
            <i className="fal fa-sign-out" /> Sign out
          </a>
        </div>
      ) : (
        <div className="request-permissions page-content">
          <div className="text-content">
            Update required
            <br />
            <br />
            <div className="smaller-font">To continue using Woo Smart Search click on the button below.</div>
          </div>
          {originRequest ?? (
            <LoadingButton
              text="Update"
              classOverride="form-btn"
              tabIndex={1}
              autoFocus
              onClick={() => onRequestPermissions?.()}
            />
          )}
        </div>
      )}
    </PageHost>
  )
}

// ─── user ────────────────────────────────────────────────────────────────────

export interface UserPageProps {
  name: string
  email: string
  onSave: (name: string) => Promise<void>
  admin?: boolean
}

export function UserPage({ name: initialName, email, onSave, admin = false }: UserPageProps): ReactElement {
  const [name, setName] = useState(initialName)
  const [submitted, setSubmitted] = useState(false)
  const [failed, setFailed] = useState(false)
  const nameMissing = name.trim().length === 0

  const save = async (): Promise<void> => {
    setSubmitted(true)
    setFailed(false)
    if (nameMissing) return
    try {
      await onSave(name)
    } catch {
      setFailed(true)
    }
  }

  return (
    <PageHost name="chrome-extension-user-page" className="popup-styles" hostClassName="non-extended-ui">
      <AuthNav admin={admin} />
      <div className="woo-form page-content">
        <div className="message">Just a little more info</div>
        <div className="popup-input-group name-wrap">
          <label className="woo-label name-label">Name</label>
          <input
            className="woo-field name"
            type="text"
            placeholder="Your name"
            name="name"
            value={name}
            onChange={(event) => {
              setName(event.target.value)
            }}
            required
          />
        </div>
        <div className="popup-input-group email-wrap">
          {/* Disabled upstream too: the email identifies the account and is not editable here. */}
          <label className="woo-label email-label">Email</label>
          <input className="woo-field email" type="email" placeholder="Your email" name="email" value={email} disabled />
        </div>
        {submitted && (nameMissing || failed) && (
          <div className="error">
            {nameMissing && <div>Please enter a name</div>}
            {failed && <div>Something went wrong...</div>}
          </div>
        )}
        <LoadingButton text="Save" classOverride="save-btn" onClick={save} />
      </div>
    </PageHost>
  )
}

// ─── sign-up ─────────────────────────────────────────────────────────────────

export interface SignUpPageProps {
  onSignUp: (name: string, email: string, password: string) => Promise<void>
  /** Upstream's `sso-required-email-validator` asks the server before it lets the form through. */
  checkSsoRequired?: (email: string) => Promise<boolean>
  /** The extension popup's `email-validator` (`isValidEmail`); see `useEmailValidProbe`. */
  checkEmailValid?: (email: string) => Promise<boolean>
  admin?: boolean
  /**
   * careero's sign-up ends with "Already a user? Sign in"; the extension popup's
   * (`whitelabel-ext/src/popup/app/sign-up/sign-up.html`) has no such line — its way back is
   * the logo. The popup passes `false`.
   */
  showSignInLink?: boolean
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
/** `password-validator.directive.ts`: at least 8 characters, one letter and one digit. */
const PASSWORD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/

export function SignUpPage({
  onSignUp,
  checkSsoRequired,
  checkEmailValid,
  admin = false,
  showSignInLink = true,
}: SignUpPageProps): ReactElement {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [failed, setFailed] = useState(false)
  // ⚠️ AS YOU TYPE, NOT ON SUBMIT. The first port probed inside `signUp()`, which is a
  // different screen: upstream's is an `$asyncValidators` entry on the email field, so the
  // answer is already in by the time the button is pressed — and the button is disabled
  // while it is not. Submitting first and asking afterwards also sends an SSO-only address
  // through a round-trip that can only fail. See `ssoRequiredProbe.ts`.
  const sso = useSsoRequiredProbe({ ...(checkSsoRequired === undefined ? {} : { check: checkSsoRequired }) })
  const ssoRequired = sso.ssoRequired
  // Same field, same `ng-model-options` debounce: both validators run off one model update.
  const validity = useEmailValidProbe(checkEmailValid)

  const missing = [name, email, password, confirmPassword].filter((value) => value.trim().length === 0).length
  const emailInvalid = (email.trim().length > 0 && !EMAIL_RE.test(email)) || validity.ssoRequired
  const passwordInvalid = password.length > 0 && !PASSWORD_RE.test(password)
  const mismatch = confirmPassword.length > 0 && confirmPassword !== password

  const signUp = async (): Promise<void> => {
    setSubmitted(true)
    setFailed(false)
    if (missing > 0 || emailInvalid || passwordInvalid || mismatch) return
    // The probe's answer, already in — and a pending one blocks, the same way the button's
    // `disabled` does for the mouse.
    if (sso.ssoRequired || sso.pending || validity.pending) return

    try {
      await onSignUp(name, email, password)
    } catch {
      setFailed(true)
    }
  }

  return (
    <PageHost name="chrome-extension-sign-up-page" className="popup-styles" hostClassName="non-extended-ui">
      <AuthNav admin={admin} />
      <div className="woo-form page-content">
        <h1 className="title">Sign up</h1>
        <input
          className="woo-field name"
          type="text"
          placeholder="Full name"
          name="name"
          value={name}
          onChange={(event) => {
            setName(event.target.value)
          }}
          tabIndex={1}
          // ⚠️ REACT'S PROP HERE, AND THE ATTRIBUTE ON LOGIN — THE OPPOSITE CHOICES, BOTH
          // MEASURED. `autofocusAttribute.ts` explains why the login screen renders the inert
          // `autofocus` ATTRIBUTE and never calls `.focus()`: production leaves that field
          // unfocused at three widths out of four, and a focus ring we draw and production
          // does not costs 916 pixels. Sign-up is the other way round. Measured against
          // production at all four widths:
          //
          //     this screen                 200     378     700    1146   worst
          //     React autoFocus (this)     0.00%   0.00%   0.00%   0.32%   0.32%
          //     the attribute, no focus    0.44%   0.44%   0.28%   0.15%   0.44%
          //
          // So production focuses this input at 200, 378 and 700, and at 1146 its focus is on
          // the SUBMIT BUTTON instead — `loading-button`'s own `ng-attr-autofocus` winning the
          // parse-time race in the one layout where it is inserted early enough. Reproducing
          // that would mean reproducing a race; focusing the input matches three widths
          // exactly and costs 0.32% at the fourth.
          //
          // Do not "fix" this for consistency with LoginPage without re-running
          // `baseline:compare` on this route: consistency here is a regression on three
          // widths out of four.
          autoFocus
          required
        />
        <input
          className="woo-field email"
          type="email"
          placeholder="Email"
          name="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value)
            sso.onChange(event.target.value)
            validity.onChange(event.target.value)
          }}
          onBlur={(event) => {
            sso.onBlur(event.target.value)
            validity.onBlur(event.target.value)
          }}
          tabIndex={2}
          required
        />
        <input
          className="woo-field password"
          type="password"
          placeholder="Password"
          name="password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value)
          }}
          tabIndex={4}
          required
        />
        <input
          className="woo-field confirm-password"
          type="password"
          placeholder="Confirm password"
          name="confirmPassword"
          value={confirmPassword}
          onChange={(event) => {
            setConfirmPassword(event.target.value)
          }}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter') void signUp()
          }}
          tabIndex={5}
          required
        />
        {/* No widget is mounted here yet — see the file header. */}
        <div id="g-recaptcha-container" />
        {submitted && (
          <SignUpErrors
            missing={missing}
            name={name}
            email={email}
            password={password}
            confirmPassword={confirmPassword}
            emailInvalid={emailInvalid}
            passwordInvalid={passwordInvalid}
            mismatch={mismatch}
            ssoRequired={ssoRequired}
            failed={failed}
          />
        )}
        <LoadingButton text="Sign up" classOverride="form-btn" tabIndex={6} disabled={sso.pending || validity.pending} onClick={signUp} />
        {showSignInLink && (
          <div className="sign-in-wrap">
            Already a user?{' '}
            <Link className="sign-in sign-up-action" to="/chrome-extension/auth/login" tabIndex={7}>
              Sign in
            </Link>
          </div>
        )}
      </div>
    </PageHost>
  )
}

/**
 * Upstream shows ONE message at a time, and the precedence is encoded in its `ng-if` chain:
 * a single missing field names that field; more than one says "fill all required fields";
 * format errors only surface once nothing is missing. Reproduced rather than simplified —
 * the diff is against a photograph of this behaviour.
 */
function SignUpErrors(props: {
  missing: number
  name: string
  email: string
  password: string
  confirmPassword: string
  emailInvalid: boolean
  passwordInvalid: boolean
  mismatch: boolean
  ssoRequired: boolean
  failed: boolean
}): ReactElement | null {
  const message = ((): string | undefined => {
    if (props.missing > 1) return 'Please fill all required fields'
    if (props.missing === 1) {
      if (props.name.trim().length === 0) return 'Please enter a name'
      if (props.email.trim().length === 0) return 'Please enter an email address'
      if (props.password.length === 0) return 'Please enter a password'
      return 'Please enter password confirmation'
    }
    if (props.emailInvalid) return 'Email address is invalid'
    if (props.ssoRequired) return 'Registration for this email address is via SSO only'
    if (props.passwordInvalid) return 'Password must contain at least 8 characters, 1 letter and 1 digit'
    if (props.mismatch) return "Passwords don't match"
    if (props.failed) return 'Something went wrong, please try again'
    return undefined
  })()

  return message === undefined ? null : (
    <div className="error">
      <div>{message}</div>
    </div>
  )
}

// ─── reset-password ──────────────────────────────────────────────────────────

export interface ResetPasswordPageProps {
  onReset: (password: string) => Promise<void>
  /** Decides which of the two completion messages shows; upstream also ORs `iFrameAuthEnabled`. */
  signedIn?: boolean
  admin?: boolean
  /**
   * The store listing for the extension this page should point at.
   *
   * ⚠️ IT IS A STORE URL, NOT A MESSAGE TARGET, which is why it is a prop rather than
   * resolved here. The id in it decides which LISTING a user lands on, and during parallel
   * launch there are two — so the caller resolves it through `extensionTarget` (CLAUDE.md 6)
   * rather than this component reaching for `careero.extensionId`, which is the legacy one.
   */
  installUrl?: string
}

/** `installUrlTemplate` upstream, verbatim. */
export const EXTENSION_STORE_URL = 'https://chromewebstore.google.com/detail/smart-search-by-woo/'

export function ResetPasswordPage({
  onReset,
  signedIn = false,
  admin = false,
  installUrl,
}: ResetPasswordPageProps): ReactElement {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [failed, setFailed] = useState(false)
  const [completed, setCompleted] = useState(false)

  const passwordMissing = password.length === 0
  const passwordInvalid = !passwordMissing && !PASSWORD_RE.test(password)
  const confirmMissing = confirmPassword.length === 0
  const mismatch = !confirmMissing && confirmPassword !== password

  const update = async (): Promise<void> => {
    setSubmitted(true)
    setFailed(false)
    if (passwordMissing || passwordInvalid || confirmMissing || mismatch) return
    try {
      await onReset(password)
    } catch {
      setFailed(true)
      return
    }
    // ⚠️ COMPLETION IS OUTSIDE THE TRY, AND UPSTREAM MOVED IT THERE AFTER A REAL TICKET
    // (`woo@8a19ab179b`, HubSpot 48598734990). The reset also notifies the extension of the
    // auth-state change, and with no extension installed — or in Incognito — that throws.
    // Sharing one catch reported an ALREADY-SUCCESSFUL reset as "Something went wrong", and
    // the retry failed too, because the token is single-use and bound to the old hash.
    //
    // This port does not notify the extension yet, so the bug is not reachable here. The
    // boundary is drawn now anyway: it is the shape that makes adding the notification safe,
    // and re-deriving it later means re-reading the ticket.
    setCompleted(true)
  }

  if (completed) {
    return (
      <PageHost name="chrome-extension-reset-password-page" className="popup-styles" hostClassName="non-extended-ui">
        <AuthNav admin={admin} linkTo={null} />
        <div className="page-content">
          {signedIn ? (
            <div className="text-content">Password was reset</div>
          ) : (
            /* ⚠️ AN INSTALL CTA, NOT AN IN-TAB LOGIN LINK. The old copy sent a user with no
               extension to a login screen that cannot help them: the whole flow completes in
               the extension popup, so there is nothing to sign into in this tab. */
            <div className="text-content">
              Password was set. Sign in from the Smart Search by Woo extension.
              <br />
              Don&apos;t have it yet?{' '}
              <a href={installUrl ?? EXTENSION_STORE_URL} target="_blank" rel="noopener noreferrer">
                Install it here
              </a>
            </div>
          )}
        </div>
      </PageHost>
    )
  }

  return (
    <PageHost name="chrome-extension-reset-password-page" className="popup-styles" hostClassName="non-extended-ui">
      <AuthNav admin={admin} linkTo={null} />
      <div className="woo-form page-content">
        <h1 className="title">Reset password</h1>
        <input
          className="woo-field password"
          type="password"
          placeholder="New password"
          name="password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value)
          }}
          required
        />
        <input
          className="woo-field confirm-password"
          type="password"
          placeholder="Confirm password"
          name="confirmPassword"
          value={confirmPassword}
          onChange={(event) => {
            setConfirmPassword(event.target.value)
          }}
          required
        />
        {submitted && (
          <div className="error">
            {passwordMissing && <div>Please enter a password</div>}
            {passwordInvalid && <div>Password must contain at least 8 characters, 1 letter and 1 digit</div>}
            {!passwordMissing && !passwordInvalid && confirmMissing && <div>Please enter password confirmation</div>}
            {!passwordMissing && !passwordInvalid && mismatch && (
              <div>Password confirmation must match password</div>
            )}
            {failed && <div>Something went wrong, please try again</div>}
          </div>
        )}
        <LoadingButton text="Reset" classOverride="form-btn" onClick={update} />
      </div>
    </PageHost>
  )
}

// ─── mfa/validate ────────────────────────────────────────────────────────────

export interface MfaValidatePageProps {
  /** Present only while enrolling. */
  qrBase64?: string
  /**
   * Whether to offer "Can't find it?", which starts an MFA RESET REQUEST.
   *
   * ⚠️ Not derived from `qrBase64 === undefined`, which is what upstream does and what the
   * first port copied. The QR is also absent when the call simply failed, so deriving it
   * invites a user to file a reset request because the tenant was briefly down. The caller
   * knows why the QR is missing; this component does not.
   */
  showResetLink?: boolean
  onVerify: (code: number) => Promise<void>
  onSignOut: () => void | Promise<unknown>
  admin?: boolean
}

export function MfaValidatePage({
  qrBase64,
  showResetLink = qrBase64 === undefined,
  onVerify,
  onSignOut,
  admin = false,
}: MfaValidatePageProps): ReactElement {
  const [code, setCode] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [failed, setFailed] = useState(false)
  const missing = code.trim().length === 0

  const verify = async (): Promise<void> => {
    setSubmitted(true)
    setFailed(false)
    if (missing) return
    try {
      // The endpoint wants a number; the input is `type="number"` upstream for the same reason.
      await onVerify(Number(code))
    } catch {
      setFailed(true)
    }
  }

  return (
    <PageHost name="chrome-extension-multi-factor-auth-validate-page" className="popup-styles" hostClassName="non-extended-ui">
      <AuthNav admin={admin} linkTo={null} />
      <div className="woo-form page-content">
        <h1 className="title">Multi-Factor Authentication</h1>
        {qrBase64 !== undefined && (
          <div className="qr">
            <div className="qr-message">Scan the QR code with your authenticator app</div>
            <img className="qr-image" src={`data:image/png;base64, ${qrBase64}`} alt="" />
          </div>
        )}
        <div className="verification-code-wrapper">
          <div className="verification-code-message">Enter your verification code</div>
          <input
            className="woo-field verification-code"
            type="number"
            placeholder="Verification code"
            name="verificationCode"
            value={code}
            onChange={(event) => {
              setCode(event.target.value)
            }}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter') void verify()
            }}
            tabIndex={1}
            autoComplete="off"
            autoFocus
            required
          />
          {showResetLink && (
            <Link className="reset-mfa" to="/chrome-extension/auth/mfa/reset-request" tabIndex={3}>
              Can&apos;t find it?
            </Link>
          )}
        </div>
        {submitted && (missing || failed) && (
          <div className="error">
            {missing && <div>Please enter the verification code</div>}
            {failed && <div>Multi factor authentication failed</div>}
          </div>
        )}
        <LoadingButton text="Verify" classOverride="form-btn" tabIndex={2} onClick={verify} />
        <div className="multi-factor-auth-validate-actions">
          <a
            className="sign-out multi-factor-auth-validate-action"
            href=""
            tabIndex={4}
            onClick={(event) => {
              event.preventDefault()
              void onSignOut()
            }}
          >
            <i className="fal fa-sign-out" /> Sign out
          </a>
        </div>
      </div>
    </PageHost>
  )
}

// ─── mfa/reset-request ───────────────────────────────────────────────────────

export interface MfaResetRequestPageProps {
  onRequestReset: (email: string) => Promise<void>
  admin?: boolean
}

export function MfaResetRequestPage({ onRequestReset, admin = false }: MfaResetRequestPageProps): ReactElement {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [failed, setFailed] = useState(false)
  const [completed, setCompleted] = useState(false)
  const invalid = !EMAIL_RE.test(email)

  const request = async (): Promise<void> => {
    setSubmitted(true)
    setFailed(false)
    if (invalid) return
    try {
      await onRequestReset(email)
      setCompleted(true)
    } catch {
      setFailed(true)
    }
  }

  return (
    <PageHost name="chrome-extension-multi-factor-auth-reset-request-page" className="popup-styles" hostClassName="non-extended-ui">
      <AuthNav admin={admin} linkTo="/chrome-extension/auth/mfa/validate" />
      {completed ? (
        <div className="page-content">
          <div className="text-content">
            Multi-Factor Authentication reset request sent, you will be contacted to complete the process
          </div>
        </div>
      ) : (
        <div className="woo-form page-content">
          <h1 className="title">Multi-Factor Authentication reset</h1>
          <input
            className="woo-field email"
            type="email"
            placeholder="Email"
            name="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
            }}
            required
          />
          {submitted && (invalid || failed) && (
            <div className="error">
              {invalid && <div>Please enter an email address</div>}
              {failed && <div>Something went wrong, please try again</div>}
            </div>
          )}
          <LoadingButton text="Send" classOverride="form-btn" onClick={request} />
        </div>
      )}
    </PageHost>
  )
}

// ─── sso/callback ────────────────────────────────────────────────────────────

export interface SsoCallbackPageProps {
  loading: boolean
  error: boolean
  /** From the `?error` / `?message` / `?user` the IdP round-trip comes back with. */
  oAuthError?: string
  oAuthMessage?: string
  oAuthUser?: string
  onTryAgain: () => void | Promise<unknown>
  admin?: boolean
}

/**
 * ⚠️ Upstream's `ng-if`s here are genuinely odd and are ported as they are: the spinner shows
 * while loading AND not errored, the error block while not loading AND errored — but "Try
 * again" shows while not loading and NOT errored, i.e. only in the state where the callback
 * quietly succeeded and the controller has already navigated away. Reproduced rather than
 * "fixed": changing it is a behaviour change, and the screen is photographed in the baseline.
 */
export function SsoCallbackPage({
  loading,
  error,
  oAuthError,
  oAuthMessage,
  oAuthUser,
  onTryAgain,
  admin = false,
}: SsoCallbackPageProps): ReactElement {
  return (
    <PageHost name="chrome-extension-sso-callback-page" className="popup-styles" hostClassName="non-extended-ui">
      <AuthNav admin={admin} linkTo={null} />
      <div className="page-content">
        {loading && !error && <i className="far fa-spinner fa-spin woo-loader" />}
        {!loading && error && (
          <div className="error">
            <div className="error-title">Something went wrong</div>
            {oAuthError !== undefined && (
              <div className="error-oauth">
                {oAuthMessage !== undefined && <div className="error-message">{oAuthMessage}</div>}
                {oAuthUser !== undefined && <div className="error-user">User: {oAuthUser}</div>}
              </div>
            )}
          </div>
        )}
        {!loading && !error && <LoadingButton text="Try again" classOverride="form-btn" onClick={onTryAgain} />}
      </div>
    </PageHost>
  )
}
